/**
 * What a WordPress site's public REST index says about connecting to it: where its application-password
 * authorize page is, whether application passwords are on, and whether the MentorKit LMS plugin is active.
 * Read in the browser without credentials; nothing is stored.
 *
 * The authorize page comes from the index rather than from the typed URL because sites that keep WordPress
 * in a subdirectory (Roots Bedrock) have the admin under `/wp` while the site itself is at the host root.
 */

export interface WordPressSiteInfo {
  /** The application-password authorize page the site reports, or null when it reports none. Always an http(s) URL. */
  authorizeUrl: string | null;
  applicationPasswordsEnabled: boolean;
  mentorKitLmsActive: boolean;
}

interface WordPressRestIndex {
  namespaces: string[];
  authentication?: { 'application-passwords'?: { endpoints?: { authorization?: unknown } } };
}

/** The REST namespace the MentorKit LMS plugin registers. */
const MENTORKIT_LMS_NAMESPACE = 'scorm-player/v1';

const REQUEST_TIMEOUT_MS = 8000;

/** Paths users paste from the admin or the API. The site is whatever comes before them. */
const WORDPRESS_PATH_TAIL = /\/(?:wp-admin|wp-login\.php|wp-json|index\.php)(?:\/.*)?$/i;

/** Local WordPress sites often have no trusted certificate, and WordPress allows application passwords over http on sites whose environment type is `local`. */
const KEEP_TYPED_HTTP = process.env.REACT_APP_ENV_LOCATION === 'dev';

/**
 * The site's base URL from whatever the user typed: https (application passwords need it, and an https page can't
 * fetch http), with no query string, fragment or trailing slash, and with a pasted `/wp-admin…`, `/wp-login.php…`,
 * `/wp-json…` or `/index.php` tail removed. A subdirectory such as Bedrock's `/wp` is kept. In development a typed
 * `http://` stays http, for local sites.
 */
export const normalizeWordPressUrl = (input: string): string => {
  const trimmed = input.trim();
  const scheme = KEEP_TYPED_HTTP && /^http:\/\//i.test(trimmed) ? 'http' : 'https';
  const address = trimmed.replace(/^(?:[a-z][a-z\d+.-]*:)?\/\/+/i, '').replace(/[?#].*$/, '');
  const pathStart = address.indexOf('/');
  const host = pathStart === -1 ? address : address.slice(0, pathStart);
  const path = pathStart === -1 ? '' : address.slice(pathStart).replace(WORDPRESS_PATH_TAIL, '').replace(/\/+$/, '');
  return `${scheme}://${host}${path}`;
};

/** The same base URL with `www.` added, or removed when it is already there. */
const toggleWww = (base: string): string => (/^https?:\/\/www\./i.test(base) ? base.replace(/^(https?:\/\/)www\./i, '$1') : base.replace(/^(https?:\/\/)/i, '$1www.'));

/** `?rest_route=` first: it reaches the API on any WordPress, whatever its permalink settings. */
const restIndexUrls = (base: string): string[] => [`${base}/?rest_route=/`, `${base}/wp-json/`];

/**
 * The REST index at `url`, or null for anything else: a network or CORS failure, a redirect, a timeout, a status
 * other than 200 or a body that isn't the index. No custom headers, so the browser sends no CORS preflight.
 */
const readRestIndex = async (url: string): Promise<WordPressRestIndex | null> => {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(url, { credentials: 'omit', signal: controller.signal });
    if (response.status !== 200) return null;
    const body = await response.json();
    return body && Array.isArray(body.namespaces) ? body : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timerId);
  }
};

/** `value` if it is an absolute http(s) URL. Callers navigate to it, so a `javascript:` URL from a hostile site must not get through. */
const httpUrlOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  try {
    return ['http:', 'https:'].includes(new URL(value).protocol) ? value : null;
  } catch {
    return null;
  }
};

const toSiteInfo = (index: WordPressRestIndex): WordPressSiteInfo => {
  // Core only lists application passwords under `authentication` when they are available on the site.
  const authorizeUrl = httpUrlOrNull(index.authentication?.['application-passwords']?.endpoints?.authorization);
  return {
    authorizeUrl,
    applicationPasswordsEnabled: authorizeUrl !== null,
    mentorKitLmsActive: index.namespaces.includes(MENTORKIT_LMS_NAMESPACE),
  };
};

/**
 * What the site at `input` reports about itself, or null when its REST index couldn't be read (not WordPress,
 * blocked, redirected, offline). Tries `?rest_route=/` and then `/wp-json/`, then both again with `www.` toggled;
 * the first index found wins. Never rejects.
 */
export const lookupWordPressSite = async (input: string): Promise<WordPressSiteInfo | null> => {
  const base = normalizeWordPressUrl(input);
  for (const url of [...restIndexUrls(base), ...restIndexUrls(toggleWww(base))]) {
    const index = await readRestIndex(url);
    if (index) return toSiteInfo(index);
  }
  return null;
};
